import {validateTextField} from '@github-ui/entity-validators'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {TextInput} from '@primer/react'
import {type ForwardedRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {GraphqlFragment, IssueFormElementRef, SharedElementProps} from '../types'
import {safeGetDefaultValue} from '../utils/default-value'
import {prefixMarkdownTitle} from '../utils/markdown-conversion'
import {uniqueIdForElementInternal} from '../utils/session-storage'
import type {
  TextInputElement_input$data,
  TextInputElement_input$key,
} from './__generated__/TextInputElement_input.graphql'
import {ElementWrapper} from './ElementWrapper'
import styles from './TextInputElement.module.css'

const DEBOUNCE_TIMEOUT_MS = 500

export type TextInputElementInternalProps = GraphqlFragment<TextInputElement_input$data> & {
  type: 'input'
  index?: number
}

type TextInputElementProps = {
  elementRef: TextInputElement_input$key
  index?: number
} & SharedElementProps

export const TextInputElement = forwardRef(
  ({elementRef, sessionStorageKey, ...props}: TextInputElementProps, ref: ForwardedRef<IssueFormElementRef>) => {
    const data = useFragment(
      graphql`
        fragment TextInputElement_input on IssueFormElementInput {
          __id
          itemId: id
          label
          descriptionHTML
          placeholder
          value
          required
        }
      `,
      elementRef,
    )

    const elementStorageKey = uniqueIdForElementInternal(sessionStorageKey, '', data.label, data.__id)
    return (
      <TextInputElementInternal ref={ref} {...data} {...props} type={'input'} sessionStorageKey={elementStorageKey} />
    )
  },
)

export const TextInputElementInternal = forwardRef(
  (
    {
      sessionStorageKey,
      itemId,
      index: indexOfFormElement,
      label,
      descriptionHTML,
      placeholder,
      required,
      value: templateDefaultValue,
      defaultValuesById,
      onFieldChange,
    }: TextInputElementInternalProps & SharedElementProps,
    ref: ForwardedRef<IssueFormElementRef>,
  ) => {
    const defaultValue = useMemo(
      () => safeGetDefaultValue({id: itemId, indexOfFormElement, value: templateDefaultValue, defaultValuesById}),
      [itemId, indexOfFormElement, templateDefaultValue, defaultValuesById],
    )

    const [textValue, setValue] = useSessionStorage<string>(sessionStorageKey, defaultValue)
    const [isUserTyping, setIsUserTyping] = useState(false)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Updates value with new default values from defaultValuesById
    useEffect(() => {
      if (onFieldChange && defaultValuesById && !isUserTyping) {
        const newDefaultValue = safeGetDefaultValue({
          id: itemId,
          indexOfFormElement,
          value: templateDefaultValue,
          defaultValuesById,
        })

        // Only update if the new default value is different from current value
        // and it's not the result of the user's own typing being reflected back
        if (newDefaultValue !== textValue && newDefaultValue !== templateDefaultValue) {
          setValue(newDefaultValue)
        }
      }
    }, [
      defaultValuesById,
      indexOfFormElement,
      isUserTyping,
      itemId,
      onFieldChange,
      setValue,
      templateDefaultValue,
      textValue,
    ])

    // Handler for text changes that updates both local state and parent component
    const handleTextChange = (value: string) => {
      setIsUserTyping(true)
      setValue(value)

      if (onFieldChange) {
        // Since these are direct changes, they should always be sent up, regardless of
        // current state
        onFieldChange(itemId, indexOfFormElement, value)
      }

      // Clear existing timeout and set a new one
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // Reset typing flag after a longer delay to prevent race conditions
      timeoutRef.current = setTimeout(() => {
        setIsUserTyping(false)
        timeoutRef.current = null
      }, DEBOUNCE_TIMEOUT_MS)
    }

    const inputRef = useRef<HTMLInputElement>(null)
    const [validationResult, setValidationResult] = useState<string | undefined>(undefined)

    useImperativeHandle(
      ref,
      () => ({
        focus: () => inputRef.current?.focus(),
        markdown: () => prefixMarkdownTitle(label, textValue),
        validate: () => {
          const validity = validateTextField(textValue, required ?? false)
          setValidationResult(validity.errorMessage)
          return validity.isValid
        },
        reset: () => {
          setValue(defaultValue)
          setValidationResult(undefined)
        },
        hasChanges: () => textValue !== defaultValue,
        getSessionStorageKey: () => sessionStorageKey,
      }),
      [defaultValue, label, required, sessionStorageKey, setValue, textValue],
    )

    useEffect(() => {
      // If we had a validation error and the user has fixed it, clear the error
      if (validationResult && validateTextField(textValue, required ?? false).isValid) {
        setValidationResult(undefined)
      }
    }, [required, textValue, validationResult])

    return (
      <ElementWrapper
        label={label}
        description={descriptionHTML}
        required={required}
        validationResult={validationResult}
      >
        {({labelId, descriptionIds}) => (
          <TextInput
            ref={inputRef}
            aria-labelledby={labelId}
            aria-describedby={descriptionIds}
            placeholder={placeholder ?? undefined}
            value={textValue}
            className={styles.issueFormTextField}
            onChange={e => {
              handleTextChange(e.target.value)
            }}
          />
        )}
      </ElementWrapper>
    )
  },
)

TextInputElement.displayName = 'TextInputElement'
TextInputElementInternal.displayName = 'TextInputElementInternal'
